import { useLayoutEffect, useRef } from "react";

import { getSearchBox } from "./searchBoxes";

const FLIGHT_MS = 220;
const EASING = "cubic-bezier(0.2, 0.7, 0.2, 1)";

interface Flight {
  ghost: HTMLElement;
  anim: Animation;
  hidden: HTMLElement[];
}

const reducedMotion = (): boolean =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function ghostFor(from: DOMRect, label: string): HTMLElement {
  const ghost = document.createElement("div");
  ghost.className = "wb-search-ghost";
  ghost.setAttribute("aria-hidden", "true");
  const icon = document.createElement("i");
  icon.className = "bi bi-search";
  const text = document.createElement("span");
  text.textContent = label;
  ghost.append(icon, text);
  ghost.style.left = `${from.left}px`;
  ghost.style.top = `${from.top}px`;
  ghost.style.width = `${from.width}px`;
  ghost.style.height = `${from.height}px`;
  return ghost;
}

/**
 * One search box at a time: when the menu opens, the page's box (the navbar's,
 * or the landing page's own) flies into the pane's search slot, and back again
 * when it closes. Both real boxes keep their layout; a ghost styled like them
 * makes the trip while they are hidden.
 */
export function useSearchFlight(menuOpen: boolean): void {
  const flight = useRef<Flight | null>(null);
  const wasOpen = useRef(menuOpen);

  useLayoutEffect(() => {
    if (wasOpen.current === menuOpen) return;
    wasOpen.current = menuOpen;
    const nav = getSearchBox("nav") ?? getSearchBox("hero");
    const menu = getSearchBox("menu");
    if (!nav || !menu || reducedMotion()) return;

    if (flight.current) {
      flight.current.anim.cancel();
      flight.current.ghost.remove();
      flight.current.hidden.forEach((el) => {
        el.style.visibility = "";
      });
      flight.current = null;
    }

    const [fromBox, toBox] = menuOpen ? [nav.box, menu.box] : [menu.box, nav.box];
    const from = fromBox.getBoundingClientRect();
    const to = toBox.getBoundingClientRect();
    const label = toBox.querySelector("input")?.placeholder ?? "";
    const ghost = ghostFor(from, label);
    document.body.appendChild(ghost);
    // The arriving box stays hidden until the ghost lands; the departing page
    // box is hidden by its own `is-away` state while the menu is open.
    const hidden = menuOpen ? [menu.box] : [nav.box];
    hidden.forEach((el) => {
      el.style.visibility = "hidden";
    });
    const anim = ghost.animate(
      [
        { left: `${from.left}px`, top: `${from.top}px`, width: `${from.width}px`, height: `${from.height}px` },
        { left: `${to.left}px`, top: `${to.top}px`, width: `${to.width}px`, height: `${to.height}px` },
      ],
      { duration: FLIGHT_MS, easing: EASING, fill: "forwards" },
    );
    const land = (): void => {
      ghost.remove();
      hidden.forEach((el) => {
        el.style.visibility = "";
      });
      if (flight.current?.ghost === ghost) flight.current = null;
    };
    anim.onfinish = land;
    anim.oncancel = land;
    flight.current = { ghost, anim, hidden };
  }, [menuOpen]);
}
